import {
  Avatar,
  Box,
  Collapse,
  Flex,
  Text,
  useColorMode,
  useDisclosure,
  // eslint-disable-next-line no-restricted-imports
  Icon as ChakraIcon,
} from '@chakra-ui/react';
import {
  ChevronDown,
  ChevronRight,
  LogOut,
  LucideIcon,
  Monitor,
  Settings,
  Upload,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from 'src/contexts/AuthContext';
import { useThemePreference } from 'src/contexts/ThemePreferenceContext';
import { buttonClickedEvent } from 'src/libs/amp';

type AccountItem = {
  label: string;
  icon: LucideIcon;
  onClick: () => void;
  closesDrawer: boolean;
};

type SidebarAccountSectionProps = {
  onNavigate?: () => void;
};

/**
 * In-place collapsible "Account" section at the bottom of the sidebar.
 */
export const SidebarAccountSection = ({
  onNavigate,
}: SidebarAccountSectionProps) => {
  const { signOut, userProfile } = useAuthContext();
  const { colorMode } = useColorMode();
  const { setPreference } = useThemePreference();
  const navigate = useNavigate();
  const { isOpen, onToggle } = useDisclosure();

  const handleItemClick = (item: AccountItem) => {
    item.onClick();
    if (item.closesDrawer) {
      onNavigate?.();
    }
  };

  const items: AccountItem[] = [
    {
      label: 'Settings',
      icon: Settings,
      onClick: () => navigate('/settings'),
      closesDrawer: true,
    },
    {
      label: 'Upload Data',
      icon: Upload,
      onClick: () => navigate('/data-upload'),
      closesDrawer: true,
    },
    {
      label: colorMode === 'dark' ? 'Light Mode' : 'Dark Mode',
      icon: Monitor,
      onClick: () => setPreference(colorMode === 'dark' ? 'light' : 'dark'),
      closesDrawer: false,
    },
    {
      label: 'Sign Out',
      icon: LogOut,
      onClick: () => {
        buttonClickedEvent({
          buttonName: 'sign-out',
        });
        signOut();
      },
      closesDrawer: true,
    },
  ];

  return (
    <Box mt="1">
      <Flex
        align="center"
        mx="4"
        px="3"
        py="2"
        borderRadius="lg"
        role="group"
        cursor="pointer"
        color="sidebar.text"
        _hover={{ bg: 'sidebar.itemHoverBg' }}
        onClick={onToggle}>
        <Avatar
          size="xs"
          boxSize="5"
          name={userProfile?.name || userProfile?.email}
          mr="3"
        />
        <Text
          fontFamily="marketingPage"
          fontSize={15}
          flex="1"
          fontWeight="medium">
          Settings
        </Text>
        <ChakraIcon
          as={isOpen ? ChevronDown : ChevronRight}
          boxSize="5"
          color="sidebar.textMuted"
        />
      </Flex>

      <Collapse in={isOpen} animateOpacity>
        <Box ml="38px" borderLeft="1px solid" borderLeftColor="surface.border">
          {items.map((item) => (
            <Box key={item.label} pl="2">
              <Flex
                align="center"
                py="2"
                pl="4"
                pr="3"
                mr="4"
                borderRadius="lg"
                cursor="pointer"
                color="sidebar.text"
                _hover={{ bg: 'sidebar.itemHoverBg' }}
                onClick={() => handleItemClick(item)}>
                <ChakraIcon
                  as={item.icon}
                  mr="3"
                  w={4}
                  h={4}
                  color="sidebar.textMuted"
                />
                <Text
                  fontFamily="marketingPage"
                  fontSize={15}
                  fontWeight="medium">
                  {item.label}
                </Text>
              </Flex>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};
